import React, { useEffect, useState, useCallback } from 'react';
import { Box, Button, Typography, Avatar, Card, CardContent, IconButton, Alert, Chip, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchKids, deleteKid } from '../../store/slices/kidSlice';
import { Delete as DeleteIcon, PersonAdd as PersonAddIcon, Warning as WarningIcon, Psychology as PsychologyIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getNeuroCardLabel, parseProfilingPaths } from '../../utils/kidProfileBadges';

const Profils = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { kids, loading, error } = useSelector((state) => state.kid);
  const { user, authInitialized, isAuthenticated } = useSelector((state) => state.auth);

  const loadKids = useCallback(() => {
    if (authInitialized && isAuthenticated && user) {
      dispatch(fetchKids());
    }
  }, [authInitialized, isAuthenticated, user, dispatch]);

  useEffect(() => {
    loadKids();
  }, [loadKids]);

  const handleAddProfile = () => {
    if (!user) {
      toast.info(
        <div>
          <p><strong>Vous devez vous connecter</strong></p>
          <p>Connectez-vous pour créer un profil d'enfant</p>
        </div>,
        {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          style: { color: '#333', background: '#ffffff', fontFamily: 'Roboto, sans-serif' }
        }
      );
      return;
    }
    
    navigate('/Profilling');
  };

  const handleDeleteProfile = async (kidId) => {
    const confirmDelete = () => toast.info(
      <div>
        <p><strong>Êtes-vous sûr de vouloir supprimer ce profil ?</strong></p>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '15px' }}>
          <Button 
            variant="contained" 
            color="error" 
            size="small"
            onClick={async () => {
              toast.dismiss();
              try {
                await dispatch(deleteKid(kidId)).unwrap();
                toast.success('Profil supprimé avec succès', {
                  theme: "light",
                  style: { color: '#333', background: '#ffffff', fontFamily: 'Roboto, sans-serif' }
                });
              } catch (error) {
                console.error('Erreur lors de la suppression:', error);
                toast.error(`Erreur: ${error.message || 'Impossible de supprimer le profil'}`, {
                  theme: "light",
                  style: { color: '#333', background: '#ffffff', fontFamily: 'Roboto, sans-serif' }
                });
              }
            }}
          >
            Supprimer
          </Button>
          <Button 
            variant="outlined" 
            size="small"
            onClick={() => toast.dismiss()}
          >
            Annuler
          </Button>
        </div>
      </div>,
      {
        position: "top-center",
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        style: { color: '#333', background: '#ffffff', width: '350px', fontFamily: 'Roboto, sans-serif' }
      }
    );
    
    confirmDelete();
  };

  const handleViewProfile = (kidId) => {
    // Find the kid in the state to check for profile data structure
    const currentKid = kids.find(kid => kid.id === kidId);
    
    if (currentKid) {
      // Vérifier si le profil est incomplet (pas de tranche d'âge)
      if (!currentKid.ages_section_id) {
        toast.info(
          <div>
            <p><strong>Profil incomplet</strong></p>
            <p>Veuillez compléter le profil de {currentKid.firstname}</p>
          </div>,
          {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            style: { color: '#333', background: '#ffffff', fontFamily: 'Roboto, sans-serif' }
          }
        );
        // Rediriger vers le profilage pour compléter
        navigate(`/Profilling/${kidId}`);
        return;
      }
      
      // Log pour debug
      if (currentKid.profil_type_id) {
        console.log('profil_type_id:', currentKid.profil_type_id);
      }
      
      // Code de compatibilité avec l'ancienne structure - à supprimer après migration complète
      if (currentKid.profil_type_ids && typeof currentKid.profil_type_ids === 'string') {
        console.log('ANCIEN FORMAT - profil_type_ids est un string JSON:', currentKid.profil_type_ids);
        
        try {
          const profileIds = JSON.parse(currentKid.profil_type_ids);
          console.log('ANCIEN FORMAT - profileIds parsés:', profileIds);
        } catch (error) {
          console.error('Erreur lors du parsing des profil_type_ids:', error);
        }
      }
    }
    
    navigate(`/profile/${kidId}`);
  };

  // Fonction pour générer les initiales du prénom
  const getInitials = (firstname) => {
    if (!firstname) return '?';
    return firstname.charAt(0).toUpperCase();
  };

  const getPedagogicalBadge = (kid) => {
    if (!kid.ages_section_id) return { label: 'À compléter', color: 'warning' };
    if (kid.profil_type) {
      const name = Array.isArray(kid.profil_type) ? kid.profil_type[0]?.name : kid.profil_type.name;
      return { label: name || 'Profil déterminé', color: 'default' };
    }
    if (kid.profil_type_id) return { label: `Profil #${kid.profil_type_id}`, color: 'default' };
    return { label: 'À faire', color: 'warning' };
  };

  const getNeuroBadge = (kid) => {
    if (!kid.ages_section_id) return null;
    if (kid.neuro_summary) {
      const detail = getNeuroCardLabel(kid.neuro_summary);
      return { label: detail || 'Terminé', color: 'success' };
    }
    if (kid.neuro_draft) return { label: 'En cours', color: 'info' };
    const paths = parseProfilingPaths(kid);
    if (paths.neuro || kid.last_neuro_evaluation_id) return { label: 'Terminé', color: 'success' };
    return { label: 'À faire', color: 'default' };
  };

  if (!authInitialized || !isAuthenticated || !user) {
    return null;
  }

  if (loading && kids.length === 0 && !error) {
    return (
      <Box
        sx={{
          width: { xs: '90%', lg: '100%' },
          maxWidth: { xs: '800px', lg: '1300px' },
          minHeight: '250px',
          border: '3px dashed #C8C8C8',
          borderRadius: '35px',
          backgroundColor: '#FFF9F0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mt: 3,
          mx: 'auto'
        }}
      >
        <Typography>Chargement des profils...</Typography>
      </Box>
    );
  }

  if (error && kids.length === 0) {
    return (
      <Box
        sx={{
          width: { xs: '90%', lg: '100%' },
          maxWidth: { xs: '800px', lg: '1300px' },
          minHeight: '250px',
          border: '3px dashed #C8C8C8',
          borderRadius: '35px',
          backgroundColor: '#FFF9F0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          mt: 3,
          mx: 'auto',
          px: 2,
        }}
      >
        <Alert severity="warning">Impossible de charger les profils. Veuillez réessayer.</Alert>
        <Button variant="contained" onClick={loadKids} sx={{ bgcolor: '#784e3c' }}>
          Réessayer
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: { xs: '90%', lg: '100%' },
        maxWidth: { xs: '800px', lg: '1300px' },
        minHeight: '250px',
        mt: 3,
        mx: 'auto'
      }}
    >
      {/* En-tête avec bouton d'ajout */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3
      }}>
        <Typography
          variant=""
          sx={{
            fontWeight: 'bold',
            color: '#784e3c',
            fontSize:'14px',

            fontFamily: 'Achiko'
          }}
        >
          Profils des enfants
        </Typography>
        {kids.length > 0 && (
          <Button
            onClick={handleAddProfile}
            startIcon={<PersonAddIcon />}
            sx={{
              backgroundColor: '#FFD88C',
              color: '#000000',
              fontWeight: 'bold',
              textTransform: 'none',
              borderRadius: '25px',
              px: 3,
              py: 1,
              boxShadow: '0px 4px 10px rgba(255, 216, 140, 0.4)',
              fontFamily: 'Achiko',
              fontSize:'10px',
              '&:hover': {
                backgroundColor: '#FFC870',
                boxShadow: '0px 6px 15px rgba(255, 216, 140, 0.6)',
                transform: 'translateY(-2px)',
              }
            }}
          >
            Ajouter un profil
          </Button>
        )}
      </Box>

      {/* Affichage des erreurs */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Liste des profils */}
      {kids.length === 0 ? (
        <Box
          sx={{
            border: '3px dashed #C8C8C8',
            borderRadius: '35px',
            backgroundColor: '#FFF9F0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            p: 4,
            minHeight: '200px'
          }}
        >
        
          <Typography
            variant="body2"
            sx={{
              color: '#666',
              textAlign: 'center',
              maxWidth: '300px'
            }}
          >
            Créez le profil de votre enfant pour commencer l'aventure  baoubab !
          </Typography>
          <Button
            onClick={handleAddProfile}
            startIcon={<PersonAddIcon />}
            sx={{
              backgroundColor: '#FFD88C',
              color: '#000000',
              fontWeight: 'bold',
              textTransform: 'none',
              borderRadius: '999px',
              px: 4,
              py: 1.2,
              mt: 1,
              fontFamily: 'Achiko',
              boxShadow: '0px 4px 12px rgba(255, 216, 140, 0.5)',
              '&:hover': {
                backgroundColor: '#FFC870',
                boxShadow: '0px 6px 16px rgba(255, 216, 140, 0.6)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            Créer un profil
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            overflowX: 'auto',
            pb: 2,
            pr: { lg: 4 },
            '&::-webkit-scrollbar': {
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#c1c1c1',
              borderRadius: '10px',
              '&:hover': {
                backgroundColor: '#a8a8a8',
              },
            },
          }}
        >
          {kids.map((kid) => (
            <Card
              key={kid.id}
              sx={{
                position: 'relative',
                minWidth: { xs: '200px', sm: '220px', lg: '260px' },
                maxWidth: { xs: '250px', lg: '300px' },
                borderRadius: '20px',
                boxShadow: '0px 4px 15px rgba(0,0,0,0.1)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0px 8px 25px rgba(0,0,0,0.15)',
                }
              }}
              onClick={() => handleViewProfile(kid.id)}
            >
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                {/* Avatar de l'enfant */}
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: '#784e3c',
                    fontSize: '2rem',
                    fontWeight: 'bold',
                    mx: 'auto',
                    mb: 2
                  }}
                >
                  {getInitials(kid.firstname)}
                </Avatar>

                {/* Informations de l'enfant */}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    color: '#333',
                    mb: 1,
                    fontFamily: 'Achiko'
                  }}
                >
                  {kid.firstname}
                </Typography>

                <Stack spacing={0.75} sx={{ mb: 1.5, alignItems: 'center' }}>
                  {(() => {
                    const ped = getPedagogicalBadge(kid);
                    return (
                      <Chip
                        size="small"
                        label={`Pédagogique · ${ped.label}`}
                        color={ped.color}
                        sx={{
                          fontSize: '0.7rem',
                          height: 22,
                          bgcolor: ped.color === 'default' ? 'rgba(120, 78, 60, 0.12)' : undefined,
                          color: ped.color === 'default' ? '#784e3c' : undefined,
                          fontWeight: 600,
                        }}
                      />
                    );
                  })()}
                  {(() => {
                    const neuro = getNeuroBadge(kid);
                    if (!neuro) return null;
                    return (
                      <Chip
                        size="small"
                        icon={<PsychologyIcon sx={{ fontSize: '14px !important' }} />}
                        label={neuro.label}
                        color={neuro.color === 'default' ? undefined : neuro.color}
                        variant={neuro.color === 'default' ? 'outlined' : 'filled'}
                        sx={{
                          fontSize: '0.7rem',
                          height: 22,
                          borderColor: neuro.color === 'default' ? '#5a7a9a' : undefined,
                          color: neuro.color === 'default' ? '#5a7a9a' : undefined,
                          fontWeight: 600,
                          '& .MuiChip-icon': { color: neuro.color === 'default' ? '#5a7a9a' : undefined },
                        }}
                      />
                    );
                  })()}
                </Stack>

                {kid.age_section ? (
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#666',
                      mb: 2
                    }}
                  >
                    {kid.age_section.name}
                  </Typography>
                ) : (
                  <Chip
                    icon={<WarningIcon />}
                    label="À compléter"
                    color="warning"
                    size="small"
                    sx={{
                      mb: 2,
                      fontWeight: 'bold',
                      animation: 'pulse 2s infinite'
                    }}
                  />
                )}

                {/* Bouton de suppression */}
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteProfile(kid.id);
                  }}
                  sx={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 0, 0, 0.1)',
                      color: 'red'
                    }
                  }}
                  size="small"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default Profils;